/**
 * batch-60 vaste-monteur (FO-440) - gedeelde types + request-schema's voor de
 * vaste monteur per werkbon-slot, de bezettingsstatus en de vervanging-monitor.
 *
 * De PURE resolver staat in `@streep/utils` (`bezettingsstatus.ts`); dit
 * bestand draagt uitsluitend de string-unions, de DTO's en de Zod-schema's die
 * API en frontends delen (CLAUDE.md §Validatie: één schema, inline fouten).
 */
import { z } from 'zod'

// ─── Statussen ───────────────────────────────────────────────────────────────

export const BEZETTINGS_STATUSSEN = [
  'VAST',
  'VERVANGEN',
  'NOG_TE_PLANNEN',
  'VASTE_MONTEUR_AFWEZIG_MAAR_GEPLAND',
  'GEPLAND',
  'ONBEMAND',
] as const
export type BezettingsStatus = (typeof BEZETTINGS_STATUSSEN)[number]

/** Interne afwezigheidsbronnen (nooit naar de klant). */
export const AFWEZIGHEID_SOORTEN = ['VERLOF', 'ZIEKTE', 'CURSUS', 'UITDIENST'] as const
export type AfwezigheidSoort = (typeof AFWEZIGHEID_SOORTEN)[number]

export const VASTE_MONTEUR_STATUSSEN = ['VOORLOPIG', 'BEVESTIGD'] as const
export type VasteMonteurStatusKey = (typeof VASTE_MONTEUR_STATUSSEN)[number]

export const VASTE_MONTEUR_BRONNEN = ['AFGELEID', 'HANDMATIG'] as const
export type VasteMonteurBronKey = (typeof VASTE_MONTEUR_BRONNEN)[number]

export const VASTE_MONTEUR_STATUS_LABELS: Record<VasteMonteurStatusKey, string> = {
  VOORLOPIG: 'Voorlopig',
  BEVESTIGD: 'Bevestigd',
}

// ─── Werkbon: vaste monteur per slot ─────────────────────────────────────────

export interface VasteMonteurEmployeeDTO {
  id: string
  naam: string
  /** FO-424 - de vaste monteur is (per zijn einddatum) uit dienst: kies een nieuwe. */
  uitDienst: boolean
}

export interface VasteMonteurDTO {
  positie: number
  employee: VasteMonteurEmployeeDTO
  status: VasteMonteurStatusKey
  bron: VasteMonteurBronKey
  afgeleidPercentage: number | null
  afgeleidDagen: number | null
  afgeleidOp: string | null
  bevestigdOp: string | null
  bevestigdDoorNaam: string | null
  /**
   * Lineage: deze werkbon heeft zelf geen koppeling maar erft die van zijn
   * hoofdbon (een afgesplitste vervangingsbon of een reeks-child is dezelfde
   * dienst). `null` = eigen koppeling.
   */
  geerfdVan: { werkbonId: string; werkbonNumber: string } | null
}

/** Een voorstel uit de afleidings-service dat nog niet is vastgelegd. */
export interface VasteMonteurVoorstelDTO {
  positie: number
  employee: { id: string; naam: string }
  percentage: number
  dagen: number
}

export interface VasteMonteurAfleidingInstellingenDTO {
  drempelPercentage: number
  minimumDagen: number
  vensterWeken: number
}

export interface WerkbonVasteMonteurOverzichtDTO {
  werkbonId: string
  /** Aantal slots (max. bemande dagregels per datum; 2 bij een 2-mans-bon). */
  slots: number
  vasteMonteurs: VasteMonteurDTO[]
  /**
   * Voorstellen voor slots ZONDER vastgelegde koppeling. Een bestaande
   * (voorlopige of bevestigde) koppeling wordt nooit stil overschreven.
   */
  voorstellen: VasteMonteurVoorstelDTO[]
  instellingen: VasteMonteurAfleidingInstellingenDTO
}

export const zetVasteMonteurSchema = z.object({
  positie: z.number().int().min(0).max(9).default(0),
  /** `null` = koppeling verwijderen (losse inzet). */
  employeeId: z.string().min(1).nullable(),
  /** Optionele toelichting bij een structurele wissel (gaat mee in de audit). */
  reden: z.string().trim().max(500).optional(),
})
export type ZetVasteMonteurInput = z.infer<typeof zetVasteMonteurSchema>

export const bevestigVasteMonteurSchema = z.object({
  positie: z.number().int().min(0).max(9).default(0),
})
export type BevestigVasteMonteurInput = z.infer<typeof bevestigVasteMonteurSchema>

// ─── Vervanging-monitor ──────────────────────────────────────────────────────

const YMD = /^\d{4}-\d{2}-\d{2}$/

/** Bereik-guard: max. 8 weken per opvraag, anders groeit de payload onbegrensd. */
export const VERVANGING_MONITOR_MAX_DAGEN = 56

export const vervangingMonitorQuerySchema = z.object({
  van: z.string().regex(YMD, 'van moet yyyy-mm-dd zijn'),
  tot: z.string().regex(YMD, 'tot moet yyyy-mm-dd zijn'),
  clientId: z.string().min(1).optional(),
  /** Default true: alleen afwijkingen (vervangen / nog te plannen / conflict). */
  alleenAfwijkingen: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v !== 'false'),
})
export type VervangingMonitorQuery = z.infer<typeof vervangingMonitorQuerySchema>

export interface VervangingMonitorRijDTO {
  dagregelId: string
  werkbonId: string
  werkbonNumber: string
  clientId: string
  klantNaam: string
  locatie: string | null
  /** `yyyy-MM-dd` */
  datum: string
  plannedStart: string
  plannedEnd: string
  /** Dienst-label (ploegsoort of diensttype) voor de kolom "Dienst". */
  dienst: string
  positie: number
  vasteMonteur: (VasteMonteurEmployeeDTO & { status: VasteMonteurStatusKey }) | null
  gepland: { id: string; naam: string } | null
  status: BezettingsStatus
  vasteMonteurAfwezig: boolean
  geplandeAfwezig: boolean
  /** Intern: waarom is de vaste monteur afwezig (voedt de badge in de monitor). */
  afwezigheidSoorten: AfwezigheidSoort[]
  /** Binnen de escalatiedrempel (default 7 dagen vanaf vandaag). */
  binnenDrempel: boolean
  /**
   * De werkbon zit in de goedkeuringsflow / is gefactureerd / verloond en kan
   * niet via het planbord hertoegewezen worden (zelfde regel als reassign).
   */
  werkbonVergrendeld: boolean
}

export interface VervangingMonitorTellersDTO {
  vast: number
  vervangen: number
  nogTePlannen: number
  conflicten: number
  /** NOG_TE_PLANNEN binnen de drempel - de teller uit de spec. */
  nogTePlannenBinnenDrempel: number
}

export interface VervangingMonitorWeekDTO {
  weekStart: string
  weekNr: number
  weekYear: number
  tellers: VervangingMonitorTellersDTO
  rijen: VervangingMonitorRijDTO[]
}

export interface VervangingMonitorKlantDTO {
  clientId: string
  klantNaam: string
  tellers: VervangingMonitorTellersDTO
  /** Werkbonnen met dagregels in het venster maar zonder vaste monteur. */
  zonderVasteMonteur: number
  /** Aantal voorlopige (nog niet bevestigde) vaste-monteur-koppelingen. */
  voorlopig: number
  weken: VervangingMonitorWeekDTO[]
}

export interface VervangingMonitorDTO {
  van: string
  tot: string
  drempelDagen: number
  alleenAfwijkingen: boolean
  klanten: VervangingMonitorKlantDTO[]
  totaal: VervangingMonitorTellersDTO
  /** Aantal rijen vóór de weergave-cap (CLAUDE.md - geen stille afkap). */
  totaalRijen: number
  afgekapt: boolean
}
